import React from 'react';
import { ArrowLeft, MessageCircle, Star, Zap, Bug, Lightbulb } from 'lucide-react';

const Changelog: React.FC = () => {
  const handleBack = () => {
    window.history.back();
  };

  const openFeedback = () => {
    // This would trigger the feedback system
    const feedbackButton = document.querySelector('[title="Send feedback"]') as HTMLButtonElement;
    if (feedbackButton) {
      feedbackButton.click();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBack}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">What's New & Coming Soon</h1>
                <p className="text-gray-600">Stay updated with the latest features and improvements</p>
              </div>
            </div>
            <button
              onClick={openFeedback}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              <span>Send Feedback</span>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="prose prose-lg max-w-none">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <div className="flex items-start space-x-3">
              <Lightbulb className="w-6 h-6 text-blue-600 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-blue-900 mb-2">Your Feedback Drives Our Development</h3>
                <p className="text-blue-800 mb-4">
                  We're constantly improving ZippyHealth.io based on your feedback and needs. 
                  Have an idea or found an issue? Click the feedback button on any page to let us know!
                </p>
                <button
                  onClick={openFeedback}
                  className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>Share Your Ideas</span>
                </button>
              </div>
            </div>
          </div>

          {/* Recently Added */}
          <section className="mb-12">
            <div className="flex items-center space-x-2 mb-6">
              <Zap className="w-6 h-6 text-green-600" />
              <h2 className="text-2xl font-bold text-gray-900">Recently Added</h2>
              <span className="text-sm text-gray-500">January 2025</span>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {/* Recipe Book */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 font-semibold">🍳</span>
                  </div>
                  <h3 className="text-lg font-semibold">Enhanced Recipe Book</h3>
                </div>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start space-x-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5" />
                    <span>100+ healthy recipes with nutrition analysis</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5" />
                    <span>AI-powered personalized recommendations</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5" />
                    <span>Health scoring for heart, diabetes, weight, gut health</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5" />
                    <span>Smart filtering by dietary restrictions and preferences</span>
                  </li>
                </ul>
                <div className="mt-4 p-3 bg-green-50 rounded border border-green-200">
                  <p className="text-sm text-green-800 font-medium">
                    What This Means For You: Easier meal planning with recipes that actually support your health goals.
                  </p>
                </div>
              </div>

              {/* Guru Connect */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-semibold">👥</span>
                  </div>
                  <h3 className="text-lg font-semibold">Guru Connect Platform</h3>
                </div>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start space-x-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5" />
                    <span>Find verified doctors, dietitians, and health coaches</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5" />
                    <span>AI-powered smart matching for your needs</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5" />
                    <span>Secure HIPAA-compliant messaging and video calls</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5" />
                    <span>Trust & verification system with credentials and reviews</span>
                  </li>
                </ul>
                <div className="mt-4 p-3 bg-blue-50 rounded border border-blue-200">
                  <p className="text-sm text-blue-800 font-medium">
                    What This Means For You: Direct access to qualified health professionals when you need guidance.
                  </p>
                </div>
              </div>

              {/* AI Health Coach */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-purple-600 font-semibold">🤖</span>
                  </div>
                  <h3 className="text-lg font-semibold">AI Health Coach</h3>
                </div>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start space-x-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5" />
                    <span>Personalized insights based on your health patterns</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5" />
                    <span>Predictive health modeling and early warnings</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5" />
                    <span>Mood and energy tracking for holistic health</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5" />
                    <span>Adaptive communication styles (encouraging, clinical, friendly, witty)</span>
                  </li>
                </ul>
                <div className="mt-4 p-3 bg-purple-50 rounded border border-purple-200">
                  <p className="text-sm text-purple-800 font-medium">
                    What This Means For You: Personalized health guidance that actually works for your unique situation.
                  </p>
                </div>
              </div>

              {/* Enhanced Analytics */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                    <span className="text-orange-600 font-semibold">📊</span>
                  </div>
                  <h3 className="text-lg font-semibold">Enhanced Analytics</h3>
                </div>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start space-x-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5" />
                    <span>Comprehensive dashboard with all health data</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5" />
                    <span>Visual trend analysis and progress tracking</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5" />
                    <span>Risk assessment with mitigation strategies</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5" />
                    <span>Correlation discovery between health factors</span>
                  </li>
                </ul>
                <div className="mt-4 p-3 bg-orange-50 rounded border border-orange-200">
                  <p className="text-sm text-orange-800 font-medium">
                    What This Means For You: Better understanding of your health patterns and what works for you.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Bug Fixes & Improvements */}
          <section className="mb-12">
            <div className="flex items-center space-x-2 mb-6">
              <Bug className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">Bug Fixes & Improvements</h2>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="grid gap-6 md:grid-cols-3">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Performance Improvements</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Pages load 40% faster</li>
                    <li>• Better search functionality</li>
                    <li>• Smoother navigation</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">User Experience Fixes</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Fixed login issues</li>
                    <li>• Improved notifications</li>
                    <li>• Enhanced mobile experience</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Data & Security</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Enhanced privacy controls</li>
                    <li>• Improved security measures</li>
                    <li>• Better data synchronization</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Coming Soon */}
          <section className="mb-12">
            <div className="flex items-center space-x-2 mb-6">
              <Zap className="w-6 h-6 text-yellow-600" />
              <h2 className="text-2xl font-bold text-gray-900">Coming Soon</h2>
              <span className="text-sm text-gray-500">Next Few Months</span>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  icon: '📱',
                  title: 'Mobile App',
                  features: ['Native iOS and Android apps', 'Offline functionality', 'Push notifications', 'Camera integration']
                },
                {
                  icon: '🔗',
                  title: 'Device Integrations',
                  features: ['Apple Health connection', 'Google Fit sync', 'Smart device support', 'Wearable integration']
                },
                {
                  icon: '🎥',
                  title: 'Enhanced Video Calls',
                  features: ['HD video quality', 'Screen sharing', 'Session recording', 'Group sessions']
                },
                {
                  icon: '📈',
                  title: 'Advanced Analytics',
                  features: ['Health predictions', 'Goal tracking', 'Comparison tools', 'Export reports']
                },
                {
                  icon: '🌍',
                  title: 'Community Features',
                  features: ['Health challenges', 'Support groups', 'Success stories', 'Expert Q&A']
                }
              ].map((item, index) => (
                <div key={index} className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                      <span className="text-gray-600 font-semibold">{item.icon}</span>
                    </div>
                    <h3 className="text-lg font-semibold">{item.title}</h3>
                  </div>
                  <ul className="space-y-2 text-gray-600">
                    {item.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start space-x-2">
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {/* Feedback Section */}
          <section className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Ideas Matter</h2>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                We're building ZippyHealth.io for you, and your feedback drives our development. 
                Share your ideas, report issues, or suggest improvements - we're listening!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={openFeedback}
                  className="flex items-center justify-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>Share Your Ideas</span>
                </button>
                <button
                  onClick={() => window.history.back()}
                  className="flex items-center justify-center space-x-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Go Back</span>
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Changelog; 
